import { lazy, Suspense, type ReactNode } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from './lib/session.js';
import { AppLayout } from './layout/AppLayout.js';
import { OnboardingProvider } from './onboarding/index.js';
import { LoginPage } from './modules/auth/LoginPage.js';
import { AcceptInvitePage } from './modules/auth/AcceptInvitePage.js';
import { Skeleton } from './components/ui/misc.js';
import { EmptyState } from './components/ui/states.js';
import { Button } from './components/ui/button.js';
import { Link } from 'react-router-dom';

// One route per module; each module lives in src/modules/<key>/index.tsx (default export).
const Today = lazy(() => import('./modules/today/index.js'));
const Pipeline = lazy(() => import('./modules/pipeline/index.js'));
const Capture = lazy(() => import('./modules/capture/index.js'));
const Draft = lazy(() => import('./modules/draft/index.js'));
const Reach = lazy(() => import('./modules/reach/index.js'));
const Media = lazy(() => import('./modules/media/index.js'));
const Strategy = lazy(() => import('./modules/strategy/index.js'));
const Products = lazy(() => import('./modules/products/index.js'));
const Laws = lazy(() => import('./modules/laws/index.js'));
const Reports = lazy(() => import('./modules/reports/index.js'));
const Review = lazy(() => import('./modules/review/index.js'));
const Tools = lazy(() => import('./modules/tools/index.js'));
const Users = lazy(() => import('./modules/users/index.js'));
const Settings = lazy(() => import('./modules/settings/index.js'));
const Setup = lazy(() => import('./modules/setup/index.js'));
const Calendar = lazy(() => import('./modules/calendar/index.js'));

function PageLoading() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      <Skeleton className="h-8 w-56" />
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
      <Skeleton className="h-72" />
    </div>
  );
}

export function App() {
  const { me, loading } = useSession();
  const { pathname } = useLocation();
  if (pathname.replace(/\/+$/, '') === '/accept-invite') return <AcceptInvitePage />;
  if (loading) return <div className="flex h-dvh items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!me) return pathname === '/login' ? <LoginPage /> : <Navigate to="/login" replace />;
  return (
    <AuthedShell>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<Navigate to="/today" replace />} />
          <Route path="/login" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<Today />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/pipeline/:id" element={<Pipeline />} />
          <Route path="/capture" element={<Capture />} />
          <Route path="/draft" element={<Draft />} />
          <Route path="/reach" element={<Reach />} />
          <Route path="/media" element={<Media />} />
          <Route path="/strategy" element={<Strategy />} />
          <Route path="/products" element={<Products />} />
          <Route path="/laws" element={<Laws />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/review" element={<Review />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/users" element={<Users />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/setup" element={<Setup />} />
          {/* Old links from before the admin-panel redesign. */}
          <Route path="/board" element={<Navigate to="/pipeline" replace />} />
          <Route path="/contacts" element={<Navigate to="/reach" replace />} />
          <Route path="/r/:id" element={<LegacyRelationshipRedirect />} />
          <Route path="*" element={<EmptyState title="Page not found" description="That address does not exist." action={<Button asChild variant="outline"><Link to="/today">Go to Today</Link></Button>} />} />
        </Routes>
      </Suspense>
    </AuthedShell>
  );
}

/**
 * The signed-in shell. OnboardingProvider must sit OUTSIDE AppLayout: the top bar's Help menu
 * (OnboardingHelpItems) reads it, and a crash there once blanked the whole app (BS-068).
 * The error boundary keeps one broken page from blanking the shell.
 */
export function AuthedShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <OnboardingProvider>
      <AppLayout>
        <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
      </AppLayout>
    </OnboardingProvider>
  );
}

function LegacyRelationshipRedirect() {
  const { pathname } = useLocation();
  return <Navigate to={`/pipeline/${pathname.split('/').pop() ?? ''}`} replace />;
}
